<script lang="ts" setup>
import type {
  EventMergeConflict,
  EventMergeRecord,
} from '@gmao/shared';
import {
  cleanDeletedEvents,
  cleanEventMergeRecord,
  getMissionId,
  MutationsMerger,
} from '@gmao/shared';
import { getEventsToMerge, handleRefusals, syncEventFromMain, updateSyncAt, uploadMissingFiles } from '@gmao/sync';
import { client } from '@operations/client';
import { downloadUnsyncedEvents } from '@operations/infra';
import * as Sentry from '@sentry/vue';
import { useAsyncFunc } from '@shared/naive-ui';
import { Close } from '@vicons/carbon';
import { computedWithControl } from '@vueuse/core';
import { useLoadingBar, useNotification } from 'naive-ui';
import { computed, ref } from 'vue';
import MutationsMergerEvent from './MutationsMergerEvent.vue';

const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'done'): void;
}>();

const merger = new MutationsMerger();
const resolves: Record<string, EventMergeConflict[] | undefined> = {};
const { data } = useAsyncFunc(() => getEventsToMerge(getMissionId()));

const events = computedWithControl(
  () => data.value,
  () => {
    return merger.run(
      [...(data.value || [])].map((record) => {
        return {
          ...structuredClone(record),
          conflicts: resolves[record.id],
        };
      }),
    );
  },
);

const filteredEvents = computed(() => events.value.filter(item => item.type !== 'stock'));

function onUpdateEvent(event: EventMergeRecord) {
  resolves[event.id] = event.conflicts;
  events.trigger();
}

const hasConflicts = computed(() => {
  for (const event of events.value) {
    if (event.conflicts) {
      for (const conflict of event.conflicts) {
        if (conflict.type !== 'duplicate' && !conflict.resolve)
          return true;
      }
    }
  }
  return false;
});

const isSending = ref(false);
const statusMessage = ref('');
const uploadProgress = ref(0);
const error = ref();

const notif = useNotification();
const loadingBar = useLoadingBar();
async function submit() {
  error.value = null;
  isSending.value = true;
  loadingBar.start();

  const missionId = getMissionId();
  const mainEvents = merger.getMainEvents();
  const localEvents = await cleanDeletedEvents(merger.getEventsToSync());

  try {
    // Sync events from main
    // One at a time, in date order: each one reads and writes the module or the stock of the previous
    for (const item of mainEvents)
      await syncEventFromMain(item);

    // Without an echo (nothing to send), the cursor would never move and the updates would stay "pending"
    const lastSyncAt = mainEvents.reduce((max, item) => (item.syncAt! > max ? item.syncAt! : max), '');
    if (lastSyncAt)
      await updateSyncAt(missionId, lastSyncAt);

    await uploadMissingFiles(localEvents, {
      onFile(index, total) {
        uploadProgress.value = 0;
        statusMessage.value = `Uploading file (${index}/${total})...`;
      },
      onProgress(percent) {
        uploadProgress.value = percent;
      },
    });

    // Sync
    uploadProgress.value = 0;
    statusMessage.value = 'Sending events...';
    const { data } = await client.operations.mutation.sync(
      {
        missionId,
        events: localEvents.map(cleanEventMergeRecord),
      },
      { notification: true },
    );

    const refusals = await handleRefusals(data.results);
    if (refusals)
      throw new Error(refusals);

    notif.success({
      title: 'Success',
      content: 'Events synchronized',
      duration: 3000,
    });
    emit('done');
  }
  catch (err: any) {
    error.value = err;
    Sentry.captureException(error, {
      data: {
        localEvents,
      },
    });
    loadingBar.error();
  }
  finally {
    loadingBar.finish();
    isSending.value = false;
  }
}
</script>

<template>
  <n-card max-w-700px>
    <template #header>
      Synchronize events
    </template>
    <template #header-extra>
      <n-button
        secondary
        size="tiny"
        mr-2
        @click="downloadUnsyncedEvents(getMissionId())"
      >
        Export unsent events
      </n-button>
      <n-button
        secondary
        circle
        size="tiny"
        @click="emit('close')"
      >
        <template #icon>
          <n-icon><Close /></n-icon>
        </template>
      </n-button>
    </template>

    <template v-if="!isSending">
      <n-timeline>
        <n-timeline-item
          v-for="event in filteredEvents"
          :key="event.id"
        >
          <MutationsMergerEvent
            :event="event"
            :events="events"
            :merger="merger"
            @update:event="onUpdateEvent"
          />
          <n-divider />
        </n-timeline-item>
      </n-timeline>
      <div text-right>
        <n-button
          v-if="!hasConflicts"
          type="primary"
          secondary
          @click="submit"
        >
          Synchronize
        </n-button>
      </div>
    </template>

    <template v-else>
      <n-spin
        :show="true"
        w-full
      >
        <template #description>
          <div>{{ statusMessage }}</div>
          <n-progress
            v-if="uploadProgress"
            type="line"
            :percentage="uploadProgress"
          />
        </template>
      </n-spin>
    </template>

    <n-alert
      v-if="error"
      title="Error"
      type="error"
      mt-2
    >
      {{ error.message || error }}
    </n-alert>
  </n-card>
</template>
